import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Target, Shield, Users, TrendingUp, ArrowRight, ChevronDown, Loader2 } from 'lucide-react';
import { useCMSContent } from '../hooks/useCMSContent';

export const AboutPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // Fetch CMS content
  const { data: companyInfo, loading: companyLoading } = useCMSContent('companyInfo');
  const { data: coreValues, loading: valuesLoading } = useCMSContent('coreValues');
  const { data: serviceAreas, loading: areasLoading } = useCMSContent('serviceAreas');
  const { data: faqs, loading: faqsLoading } = useCMSContent('faqs');

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Show loading state
  if ((companyLoading && !companyInfo) || (faqsLoading && !faqs)) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="animate-spin" size={48} color="var(--brand-primary)" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-page)' }} data-testid="about-page">
      {/* Hero */}
      <section style={{
        position: 'relative',
        backgroundColor: 'var(--bg-card)',
        padding: '100px 0 80px'
      }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <h1 className="heading-1" style={{ marginBottom: '28px' }}>
              About Laurel Run Management Group
            </h1>
            <p className="body-large" style={{ color: 'var(--neutral-light)', lineHeight: 1.8 }}>
              {companyInfo?.missionStatement || 'Laurel Run Management Group is dedicated to providing superior property management services that protect and enhance our clients\' real estate investments.'}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="section-spacing">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <img
                src="https://images.unsplash.com/photo-1606788075819-9574a6edfab3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBob21lfGVufDB8fHx8MTc3MDQxNDM1OHww&ixlib=rb-4.1.0&q=85"
                alt="Our Team"
                style={{
                  width: '100%',
                  height: '500px',
                  objectFit: 'cover',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}
              />
            </div>

            <div>
              <h2 className="heading-2" style={{ marginBottom: '28px' }}>
                Our Core Values
              </h2>
              <p className="body-medium" style={{ marginBottom: '30px', color: 'var(--neutral-light)' }}>
                These principles guide every decision we make and every service we provide.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} data-testid="core-values-list">
                {(coreValues || []).map((valueItem, index) => (
                  <div key={valueItem.id || valueItem.documentId || index} style={{
                    padding: '20px',
                    backgroundColor: 'var(--bg-card)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-medium)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: 'var(--brand-primary)',
                      borderRadius: '50%',
                      flexShrink: 0
                    }} />
                    <span style={{
                      color: 'var(--neutral-light)',
                      fontSize: '1.05rem',
                      lineHeight: 1.6
                    }}>
                      {valueItem.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience */}
      <section className="section-spacing" style={{ backgroundColor: 'var(--bg-card)' }}>
        <div className="container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 className="heading-2" style={{ marginBottom: '28px' }}>
              Experience & Expertise
            </h2>
            <p className="body-large" style={{
              color: 'var(--neutral-light)',
              lineHeight: 1.8,
              marginBottom: '50px'
            }}>
              {companyInfo?.experienceDescription || 'With extensive experience in both residential and commercial property management, our team brings the knowledge and systems necessary to handle properties of all types and sizes.'}
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '40px',
              marginTop: '50px'
            }}>
              {[
                { icon: Target, label: 'Professional Focus' },
                { icon: Shield, label: 'Asset Protection' },
                { icon: Users, label: 'Client Service' },
                { icon: TrendingUp, label: 'Proven Results' }
              ].map((item, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <item.icon size={40} color="var(--brand-primary)" strokeWidth={1.5} style={{ marginBottom: '20px', display: 'inline-block' }} />
                  <p style={{
                    color: 'var(--text-primary)',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}>
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-spacing">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '50px' }}>
            <h2 className="heading-1" style={{ marginBottom: '20px' }}>
              Service Areas
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Professional property management throughout the region
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px',
            maxWidth: '1000px',
            margin: '0 auto'
          }} data-testid="service-areas-grid">
            {(serviceAreas || []).map((area, index) => (
              <div
                key={area.id || area.documentId || index}
                style={{
                  backgroundColor: 'var(--bg-card)',
                  padding: '30px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-medium)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}
                className="card-hover"
              >
                <p style={{
                  color: 'var(--brand-primary)',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  margin: 0
                }}>
                  {area.name}
                </p>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '50px',
            textAlign: 'center',
            padding: '40px',
            backgroundColor: 'var(--bg-card)',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)'
          }}>
            <p style={{
              color: 'var(--neutral-light)',
              fontSize: '1.1rem',
              margin: 0
            }}>
              Don't see your area listed? Contact us to discuss your property management needs.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-spacing" style={{ backgroundColor: 'var(--bg-card)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '50px' }}>
              <h2 className="heading-1" style={{ marginBottom: '20px' }}>
                Frequently Asked Questions
              </h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                Common questions about our property management services
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }} data-testid="faq-list">
              {(faqs || []).map((item, index) => (
                <div key={item.id || item.documentId || index} style={{
                  borderBottom: '1px solid var(--border-medium)',
                  paddingBottom: '10px'
                }}>
                  <button
                    onClick={() => toggleFaq(index)}
                    data-testid={`faq-question-${index}`}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '20px 0',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <span style={{
                      fontFamily: 'Arial, sans-serif',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      color: 'var(--brand-primary)'
                    }}>
                      {item.question}
                    </span>
                    <ChevronDown
                      size={24}
                      color="var(--brand-primary)"
                      style={{
                        transform: openFaq === index ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        flexShrink: 0,
                        marginLeft: '15px'
                      }}
                    />
                  </button>
                  {openFaq === index && (
                    <div style={{
                      color: 'var(--neutral-light)',
                      fontSize: '1rem',
                      lineHeight: 1.8,
                      paddingBottom: '20px',
                      paddingTop: '10px',
                      paddingRight: '40px',
                      animation: 'slideDown 0.3s ease'
                    }}>
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <style>{`
              @keyframes slideDown {
                from {
                  opacity: 0;
                  transform: translateY(-10px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}</style>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing" style={{
        backgroundColor: 'var(--secondary-olive)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 className="heading-1" style={{ marginBottom: '24px', color: 'var(--brand-primary)' }}>
            Let's Work Together
          </h2>
          <p className="body-large" style={{
            marginBottom: '40px',
            maxWidth: '700px',
            margin: '0 auto 40px',
            color: 'var(--neutral-light)'
          }}>
            Ready to experience professional property management? Contact us today to learn more.
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary">
              Contact Us
              <ArrowRight size={20} style={{ marginLeft: '8px' }} />
            </Link>
            <Link to="/services" className="btn-secondary">
              Our Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
